import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

const ARES_API = "https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/";

function formatBmsl(val) {
  if (!val) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return n.toLocaleString("cs-CZ", { maximumFractionDigits: 0 }) + " Kč";
}

function formatDate(val) {
  if (!val) return "—";
  try {
    return new Date(val).toLocaleDateString("cs-CZ");
  } catch {
    return val;
  }
}

function getLastNote(activities) {
  if (!activities || activities.length === 0) return "—";
  const last = [...activities].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  return last.text || "—";
}

export default function LeadtoolDashboard() {
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [view, setView] = useState("dashboard"); // "dashboard" | "add-lead"

  // Add lead form
  const [leadForm, setLeadForm] = useState({
    ico: "",
    companyName: "",
    address: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    note: ""
  });
  const [aresLoading, setAresLoading] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [leadSuccess, setLeadSuccess] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);

  const token = localStorage.getItem("supplierToken");

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    setLoadingLeads(true);
    try {
      const res = await fetch(`${API_URL}/supplier/leads`, {
        headers: { "x-supplier-token": token }
      });
      if (res.status === 401) {
        localStorage.removeItem("supplierToken");
        localStorage.removeItem("supplierData");
        navigate("/leadtool");
        return;
      }
      const data = await res.json();
      if (data.success) setCompanies(data.companies || []);
    } catch (e) {
      console.error("Chyba při načítání leadů:", e);
    } finally {
      setLoadingLeads(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/leadtool");
      return;
    }
    const stored = localStorage.getItem("supplierData");
    if (stored) {
      try { setSupplier(JSON.parse(stored)); } catch {}
    }
    // Verify token
    fetch(`${API_URL}/supplier/me`, {
      headers: { "x-supplier-token": token }
    }).then(res => {
      if (res.status === 401) {
        localStorage.removeItem("supplierToken");
        localStorage.removeItem("supplierData");
        navigate("/leadtool");
      } else {
        return res.json().then(data => {
          setSupplier(data);
          localStorage.setItem("supplierData", JSON.stringify(data));
        });
      }
    }).catch(() => {});

    fetchLeads();
  }, [token, navigate, fetchLeads]);

  const handleLogout = () => {
    localStorage.removeItem("supplierToken");
    localStorage.removeItem("supplierData");
    navigate("/leadtool");
  };

  const lookupAres = async () => {
    const ico = leadForm.ico.trim();
    if (!ico || ico.length < 6) {
      setLeadError("Zadejte platné IČO (6–8 číslic)");
      return;
    }
    setAresLoading(true);
    setLeadError("");
    try {
      const res = await fetch(`${ARES_API}${ico}`);
      if (!res.ok) throw new Error("IČO nebylo nalezeno v registru ARES");
      const data = await res.json();
      const name = data.obchodniJmeno || data.nazev || "";
      const sidlo = data.sidlo || {};
      const address = [
        sidlo.nazevUlice,
        sidlo.cisloDomovni ? `${sidlo.cisloDomovni}${sidlo.cisloOrientacni ? "/" + sidlo.cisloOrientacni : ""}` : "",
        sidlo.nazevObce,
        sidlo.psc ? String(sidlo.psc).replace(/(\d{3})(\d{2})/, "$1 $2") : ""
      ].filter(Boolean).join(", ");
      setLeadForm(f => ({ ...f, companyName: name, address }));
    } catch (e) {
      setLeadError(e.message || "Chyba při vyhledávání v ARES");
    } finally {
      setAresLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadError("");
    setLeadSuccess("");
    if (!leadForm.ico || !leadForm.companyName) {
      setLeadError("IČO a název firmy jsou povinné");
      return;
    }
    setLeadLoading(true);
    try {
      const res = await fetch(`${API_URL}/supplier/lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-supplier-token": token
        },
        body: JSON.stringify(leadForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba při odesílání leadu");
      if (data.isDuplicate) {
        setLeadSuccess("Lead byl odeslán. ⚠️ Upozornění: firma s tímto IČO již v systému existuje — byl vytvořen duplicitní záznam a do původní firmy přidána poznámka.");
      } else {
        setLeadSuccess("Lead byl úspěšně odeslán!");
      }
      setLeadForm({ ico: "", companyName: "", address: "", contactName: "", contactPhone: "", contactEmail: "", note: "" });
      fetchLeads();
      setTimeout(() => {
        setView("dashboard");
        setLeadSuccess("");
      }, 2000);
    } catch (e) {
      setLeadError(e.message);
    } finally {
      setLeadLoading(false);
    }
  };

  // Statistics
  const closed = companies.filter(c => String(c.forecast) === "100");
  const inProgress = companies.filter(c => String(c.forecast) !== "100");
  const totalBmsl = closed.reduce((sum, c) => sum + (parseFloat(c.bmsl) || 0), 0);

  // Monthly stats
  const monthlyStats = {};
  closed.forEach(c => {
    if (c.closedDate) {
      const month = c.closedDate.substring(0, 7);
      if (!monthlyStats[month]) monthlyStats[month] = { count: 0, bmsl: 0 };
      monthlyStats[month].count++;
      monthlyStats[month].bmsl += parseFloat(c.bmsl) || 0;
    }
  });
  const sortedMonths = Object.keys(monthlyStats).sort().reverse();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/vyjedname-logo.png" alt="Vyjednáme" className="h-8 object-contain" />
            <div className="h-7 w-px bg-gray-200" />
            <img src="/vodafone-partner-business.png" alt="Vodafone Business Partner" className="h-9 object-contain" />
            {supplier && (
              <>
                <div className="h-7 w-px bg-gray-200" />
                <span className="text-sm font-semibold text-gray-800">{supplier.companyName}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setView("add-lead"); setLeadError(""); setLeadSuccess(""); }}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              + Přidat lead
            </button>
            <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
              Odhlásit
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">

        {/* Add Lead View */}
        {view === "add-lead" && (
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setView("dashboard")} className="text-gray-400 hover:text-gray-600">
                ← Zpět
              </button>
              <h2 className="text-xl font-bold text-gray-900">Přidat nový lead</h2>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              {leadError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{leadError}</div>
              )}
              {leadSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">{leadSuccess}</div>
              )}

              <form onSubmit={handleLeadSubmit} className="space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Identifikace firmy</h3>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">IČO *</label>
                      <input
                        type="text"
                        value={leadForm.ico}
                        onChange={e => setLeadForm(f => ({ ...f, ico: e.target.value }))}
                        placeholder="12345678"
                        maxLength={8}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={lookupAres}
                        disabled={aresLoading}
                        className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {aresLoading ? "Hledám..." : "ARES"}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Název firmy *</label>
                  <input
                    type="text"
                    value={leadForm.companyName}
                    onChange={e => setLeadForm(f => ({ ...f, companyName: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresa</label>
                  <input
                    type="text"
                    value={leadForm.address}
                    onChange={e => setLeadForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div className="border-t pt-5">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Kontaktní osoba</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Jméno</label>
                      <input
                        type="text"
                        value={leadForm.contactName}
                        onChange={e => setLeadForm(f => ({ ...f, contactName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                      <input
                        type="tel"
                        value={leadForm.contactPhone}
                        onChange={e => setLeadForm(f => ({ ...f, contactPhone: e.target.value }))}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={leadForm.contactEmail}
                        onChange={e => setLeadForm(f => ({ ...f, contactEmail: e.target.value }))}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Poznámka</label>
                  <textarea
                    value={leadForm.note}
                    onChange={e => setLeadForm(f => ({ ...f, note: e.target.value }))}
                    rows={3}
                    placeholder="Informace o zákazníkovi, o jeho tarifu, potenciálu…"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={leadLoading}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
                  >
                    {leadLoading ? "Odesílám..." : "Odeslat lead"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("dashboard")}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors text-sm"
                  >
                    Zrušit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Dashboard View */}
        {view === "dashboard" && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Celkem leadů</p>
                <p className="text-3xl font-bold text-gray-900">{companies.length}</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">FC = 100 (uzavřeno)</p>
                <p className="text-3xl font-bold text-green-600">{closed.length}</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">V procesu</p>
                <p className="text-3xl font-bold text-blue-600">{inProgress.length}</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Celkové BMSL</p>
                <p className="text-2xl font-bold text-gray-900">{totalBmsl > 0 ? formatBmsl(totalBmsl) : "—"}</p>
              </div>
            </div>

            {/* Měsíční přehled */}
            {sortedMonths.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Měsíční přehled uzavřených případů</h3>
                <div className="space-y-2">
                  {sortedMonths.map(month => (
                    <div key={month} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-700 font-medium">{month}</span>
                      <div className="flex items-center gap-6 text-sm">
                        <span className="text-gray-500"><span className="font-semibold text-gray-900">{monthlyStats[month].count}</span> případů</span>
                        <span className="text-gray-500">BMSL: <span className="font-semibold text-gray-900">{formatBmsl(monthlyStats[month].bmsl)}</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tabulka uzavřených (FC=100) */}
            {closed.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">Uzavřené případy (FC = 100)</h3>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">{closed.length} případů</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Firma</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">IČO</th>
                        <th className="text-right px-4 py-3 font-medium text-gray-600">BMSL</th>
                        <th className="text-center px-4 py-3 font-medium text-gray-600">Datum uzavření</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Poznámka</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closed.map(c => (
                        <tr key={c._id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                          <td className="px-4 py-3 text-gray-600">{c.ico || "—"}</td>
                          <td className="px-4 py-3 text-right font-semibold text-green-700">{formatBmsl(c.bmsl)}</td>
                          <td className="px-4 py-3 text-center text-gray-600">{formatDate(c.closedDate)}</td>
                          <td className="px-4 py-3 text-gray-500 max-w-xs truncate" title={getLastNote(c.activities)}>{getLastNote(c.activities)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-green-50">
                        <td colSpan={2} className="px-4 py-3 font-semibold text-gray-700">Celkem</td>
                        <td className="px-4 py-3 text-right font-bold text-green-700">{formatBmsl(totalBmsl)}</td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Tabulka zbývajících leadů */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Leady v procesu</h3>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">{inProgress.length} leadů</span>
              </div>
              {loadingLeads ? (
                <div className="px-5 py-10 text-center text-gray-400 text-sm">Načítám…</div>
              ) : inProgress.length === 0 ? (
                <div className="px-5 py-10 text-center">
                  <p className="text-gray-500 text-sm mb-3">Zatím žádné aktivní leady</p>
                  <button
                    onClick={() => { setView("add-lead"); setLeadError(""); setLeadSuccess(""); }}
                    className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors"
                  >
                    Přidat první lead
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Firma</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">IČO</th>
                        <th className="text-center px-4 py-3 font-medium text-gray-600">FC</th>
                        <th className="text-right px-4 py-3 font-medium text-gray-600">BMSL</th>
                        <th className="text-center px-4 py-3 font-medium text-gray-600">Datum</th>
                        <th className="text-center px-4 py-3 font-medium text-gray-600">Stav</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Poznámka</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inProgress.map(c => (
                        <tr key={c._id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                          <td className="px-4 py-3 text-gray-600">{c.ico || "—"}</td>
                          <td className="px-4 py-3 text-center">
                            {c.forecast ? (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                String(c.forecast) === "100"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-yellow-100 text-yellow-700"
                              }`}>{c.forecast}%</span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">{c.bmsl ? formatBmsl(c.bmsl) : "—"}</td>
                          <td className="px-4 py-3 text-center text-gray-500">{formatDate(c.createdAt)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{c.status || "—"}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 max-w-xs truncate" title={getLastNote(c.activities)}>{getLastNote(c.activities)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer branding */}
      <footer className="mt-16 border-t border-gray-200 bg-white py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-center gap-4">
          <img src="/vyjedname-logo.png" alt="Vyjednáme" className="h-6 object-contain opacity-60" />
          <span className="text-xs text-gray-400">Partnerský leadový program Vyjednáme s.r.o.</span>
        </div>
      </footer>
    </div>
  );
}
